import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Upload, Layers, RefreshCw, Sparkles, X, ArrowRight } from 'lucide-react';

// Components
import Header from '../components/Header';
import UploadZone from '../components/UploadZone';
import DriveStatusBanner from '../components/DriveStatusBanner';
import DashboardTabs from '../components/DashboardTabs';
import InvoicesTab from '../components/InvoicesTab';
import RecurringPayments from '../components/RecurringPayments';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import RecurringInvoices from '../components/RecurringInvoices';
import TaxReports from '../components/TaxReports';
import AIChatAssistant from '../components/AIChatAssistant';
import IncomeTab from '../components/IncomeTab';
import CreateInvoiceTab from '../components/CreateInvoiceTab';
import BankTransactionsTab from '../components/BankTransactionsTab';
import PayrollTab from '../components/PayrollTab';

// Modals
import InvoiceReviewModal from '../components/InvoiceReviewModal';
import DriveSetup from '../components/DriveSetup';
import ExportModal from '../components/ExportModal';
import BulkUploadModal from '../components/BulkUploadModal';
import DuplicateConfirmModal from '../components/DuplicateConfirmModal';
import RecurringPaymentPrompt from '../components/RecurringPaymentPrompt';
import OnboardingModal from '../components/OnboardingModal';

// Hooks
import { useInvoices } from '../hooks/useInvoices';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Refresh indicator for pull-to-refresh
const RefreshIndicator = ({ isRefreshing }) => (
  <div className="flex items-center justify-center py-4">
    <RefreshCw className={`w-6 h-6 text-indigo-600 ${isRefreshing ? 'animate-spin' : ''}`} />
    <span className="ml-2 text-sm text-slate-600 dark:text-slate-400">
      {isRefreshing ? 'Refreshing...' : 'Pull to refresh'}
    </span>
  </div>
);

function DashboardPage() {
  const navigate = useNavigate();
  
  // User state
  const [user, setUser] = useState(null);
  
  // UI state
  const [activeTab, setActiveTab] = useState('expenses');
  const [showInvoiceDrop, setShowInvoiceDrop] = useState(true); // Show upload zone by default
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Drive state
  const [driveConnected, setDriveConnected] = useState(false);
  const [showDriveSetup, setShowDriveSetup] = useState(false);
  
  // Modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [showUpgradeBanner, setShowUpgradeBanner] = useState(true);
  
  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Invoice management hook
  const {
    invoices,
    filteredInvoices,
    isProcessing,
    selectedInvoice,
    showReviewModal,
    duplicateInfo,
    recurringPaymentPrompt,
    fetchInvoices,
    handleSearch,
    handleFilterChange,
    handleFileSelect,
    handleDuplicateConfirm,
    handleDuplicateCancel,
    handleSaveInvoice,
    handleEditInvoice,
    handleDeleteInvoice,
    handleDownloadInvoice,
    handleDownloadFormat,
    handleMarkPaid,
    handleMarkUnpaid,
    closeReviewModal,
    closeRecurringPrompt,
  } = useInvoices();

  // Initial data fetch
  useEffect(() => {
    fetchUser();
    fetchInvoices();
    checkDriveStatus();
    fetchSubscription();
    checkOnboarding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkOnboarding = () => {
    const completed = localStorage.getItem('onboarding_completed');
    if (!completed) {
      // Small delay to let the page load first
      setTimeout(() => setShowOnboarding(true), 500);
    }
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      navigate('/login');
    }
  };

  const checkDriveStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/drive-status`, { withCredentials: true });
      setDriveConnected(response.data.connected);
    } catch (error) {
      console.error('Failed to check Drive status:', error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/current`, { withCredentials: true });
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };

  // Pull-to-refresh handler
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([fetchInvoices(), checkDriveStatus()]);
      toast.success('Refreshed!', { duration: 1500 });
    } catch (error) {
      console.error('Refresh failed:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchInvoices]);

  // Drive setup handlers
  const handleDriveSetupComplete = () => {
    setShowDriveSetup(false);
    setDriveConnected(true);
    toast.success('Google Drive connected! Your invoices will now be automatically backed up.');
  };

  const handleDriveSetupSkip = () => {
    setShowDriveSetup(false);
    toast.info('You can connect Google Drive later from your profile settings.');
  };

  // Export to Drive handler
  const handleExportToDrive = async (invoice) => {
    try {
      if (!driveConnected) {
        toast.error('Google Drive not connected. You can download the Excel file locally instead.');
        return;
      }

      await axios.post(
        `${API}/invoices/${invoice.invoice_id}/export`,
        {},
        { withCredentials: true }
      );

      toast.success('Successfully exported to Google Drive!');
      await fetchInvoices();
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.detail || 'Failed to export to Google Drive');
    }
  };

  // Handle recurring payment added - switch to payments tab
  const handleRecurringPaymentAdded = () => {
    closeRecurringPrompt();
    setActiveTab('payments');
  };

  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'expenses':
        return (
          <InvoicesTab
            invoices={invoices}
            filteredInvoices={filteredInvoices}
            driveConnected={driveConnected}
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            onEdit={handleEditInvoice}
            onDownload={handleDownloadInvoice}
            onDownloadFormat={handleDownloadFormat}
            onExportToDrive={handleExportToDrive}
            onDelete={handleDeleteInvoice}
            onShowExportModal={() => setShowExportModal(true)}
            onMarkPaid={handleMarkPaid}
            onMarkUnpaid={handleMarkUnpaid}
          />
        );
      case 'payments':
        return <RecurringPayments />;
      case 'income':
        return <IncomeTab />;
      case 'bank':
        return <BankTransactionsTab />;
      case 'payroll':
        return <PayrollTab />;
      case 'create-invoice':
        return <CreateInvoiceTab />;
      case 'analytics':
        return <AnalyticsDashboard />;
      case 'tax':
        return <TaxReports />;
      case 'recurring':
        return <RecurringInvoices />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 dark:from-slate-950 dark:to-slate-900">
      <Header user={user} />

      {/* Subscription Status Banner - Different colors per tier */}
      {showUpgradeBanner && subscription && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`text-white ${
            subscription.tier === 'team' 
              ? 'bg-gradient-to-r from-amber-500 to-yellow-500' 
              : subscription.tier === 'pro'
              ? 'bg-gradient-to-r from-emerald-500 to-green-500'
              : 'bg-gradient-to-r from-indigo-600 to-purple-600'
          }`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className={`w-5 h-5 ${subscription.tier === 'team' ? 'text-yellow-200' : subscription.tier === 'pro' ? 'text-emerald-200' : 'text-amber-300'}`} />
                <p className="text-sm sm:text-base font-medium">
                  {subscription.tier === 'team' ? (
                    <>You&apos;re on the <span className="font-bold">Team</span> plan — Unlimited everything!</>
                  ) : subscription.tier === 'pro' ? (
                    <>You&apos;re on the <span className="font-bold">Pro</span> plan ({subscription?.invoices_used_this_month || 0}/100 invoices used)</>
                  ) : (
                    <>
                      You&apos;re on the <span className="font-bold">Free</span> plan ({subscription?.invoices_used_this_month || 0}/10 invoices used)
                      <span className="hidden sm:inline"> — Unlock unlimited invoices, analytics & AI features!</span>
                    </>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {subscription.tier === 'free' && (
                  <Link 
                    to="/settings?tab=billing"
                    className="flex items-center gap-1 px-3 py-1.5 bg-white text-indigo-700 rounded-lg text-sm font-semibold hover:bg-indigo-50 transition-colors"
                    data-testid="upgrade-banner-btn"
                  >
                    Upgrade
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
                {subscription.tier !== 'free' && (
                  <Link 
                    to="/settings?tab=billing"
                    className={`flex items-center gap-1 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors`}
                  >
                    Manage Plan
                  </Link>
                )}
                <button 
                  onClick={() => setShowUpgradeBanner(false)}
                  className="p-1 hover:bg-white/20 rounded transition-colors"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Trial Banner */}
      {subscription?.is_trial && (
        <div className="bg-amber-500 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
            <div className="flex items-center justify-center gap-2 text-sm">
              <Sparkles className="w-4 h-4" />
              <span>
                Pro Trial — {subscription?.trial_ends_at ? `Ends ${new Date(subscription.trial_ends_at).toLocaleDateString()}` : '14 days remaining'}
              </span>
              <Link to="/settings?tab=billing" className="underline font-semibold ml-2">
                Subscribe to keep features
              </Link>
            </div>
          </div>
        </div>
      )}

      <PullToRefresh
        onRefresh={handleRefresh}
        pullingContent={<RefreshIndicator isRefreshing={false} />}
        refreshingContent={<RefreshIndicator isRefreshing={true} />}
        resistance={2.5}
        maxPullDownDistance={100}
        pullDownThreshold={60}
      >
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Drive Connection Status */}
            <DriveStatusBanner driveConnected={driveConnected} />

            {/* Main Tabs - Now at top */}
            <div className="mb-6">
              <DashboardTabs
                activeTab={activeTab}
                onTabChange={(tab) => {
                  setActiveTab(tab);
                  setShowInvoiceDrop(false);
                }}
                invoiceCount={invoices.length}
                onInvoiceDropClick={() => setShowInvoiceDrop(true)}
                showInvoiceDrop={showInvoiceDrop}
              />
            </div>

            {/* Invoice Drop Section - Shows when Invoice Drop tab is active */}
            {showInvoiceDrop && (
              <div className="mb-8">
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Drop your PDF or screenshot to extract invoice data automatically
                    </p>
                    <button
                      onClick={() => setShowBulkUpload(true)}
                      className="px-3 py-1.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-900/60 transition-all font-medium rounded-lg flex items-center space-x-1 text-xs"
                      data-testid="bulk-upload-button"
                    >
                      <Layers className="w-3 h-3" />
                      <span>Bulk Upload</span>
                    </button>
                  </div>
                </div>
                <UploadZone onFileSelect={handleFileSelect} isProcessing={isProcessing} />
              </div>
            )}

            {/* Tab Content */}
            {!showInvoiceDrop && renderTabContent()}
          </motion.div>
        </main>
      </PullToRefresh>

      {/* Modals */}
      {showReviewModal && selectedInvoice && (
        <InvoiceReviewModal
          invoice={selectedInvoice}
          onClose={closeReviewModal}
          onSave={handleSaveInvoice}
        />
      )}

      {showDriveSetup && (
        <DriveSetup 
          onComplete={handleDriveSetupComplete}
          onSkip={handleDriveSetupSkip}
        />
      )}

      {showExportModal && (
        <ExportModal onClose={() => setShowExportModal(false)} />
      )}

      {showBulkUpload && (
        <BulkUploadModal 
          onClose={() => setShowBulkUpload(false)}
          onComplete={fetchInvoices}
        />
      )}

      {duplicateInfo && (
        <DuplicateConfirmModal
          duplicateInfo={duplicateInfo}
          onConfirm={handleDuplicateConfirm}
          onCancel={handleDuplicateCancel}
        />
      )}

      {recurringPaymentPrompt && (
        <RecurringPaymentPrompt
          invoice={recurringPaymentPrompt}
          onClose={closeRecurringPrompt}
          onAdded={handleRecurringPaymentAdded}
        />
      )}

      {/* Onboarding Modal for First-Time Users */}
      {showOnboarding && (
        <OnboardingModal onClose={() => setShowOnboarding(false)} />
      )}

      {/* AI Chat Assistant - Floating Window */}
      <AIChatAssistant />
    </div>
  );
}

export default DashboardPage;
